import React, { useEffect, useMemo, useRef, useState } from 'react';
import TsunamiMeter from '../components/TsunamiMeter';
import InfoBar from '../components/InfoBar';
import ChatWBG from '../components/ChatWBG';
import StatCard from '../components/StatCard';
import OverviewLineChart from '../components/charts/OverviewLineChart';
import WinningLosingDonut from '../components/charts/WinningLosingDonut';
import CalendarHeatmap from '../components/charts/CalendarHeatmap';
import { loadExternalScript } from '../utils/scriptLoader';
import { PageRevealStyles, usePageReveal } from '../components/PageReveal';
import { DISCORD_CSV_BY_YEAR, YTD_CSV_BY_YEAR, getCsvUrls } from '../utils/dataSources';

const D3_URL = 'https://d3js.org/d3.v7.min.js';
const YTD_CSVS = getCsvUrls(YTD_CSV_BY_YEAR);
const DISCORD_CSVS = getCsvUrls(DISCORD_CSV_BY_YEAR);

const formatCurrency = (value) => {
  if (value == null) return '—';
  const absolute = Math.abs(value);
  const formatted = absolute.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

const LegendStyles = () => (
  <style>{`
    .chart-legend{ display:flex; align-items:center; gap:20px; margin-bottom:20px; flex-wrap:wrap; }
    .chart-legend .legend-item{ display:flex; align-items:center; gap:8px; }
    .chart-legend--inline{ flex-direction:row !important; align-items:center !important; }
    @media (max-width: 600px){
      .chart-legend{ flex-direction:column; align-items:flex-start; gap:10px; }
      .chart-legend.chart-legend--inline{ flex-direction:row !important; }
    }
  `}</style>
);

const applyYearlyChanges = (series) => {
  const byYear = new Map();
  series.forEach((entry) => {
    const year = entry.date.getFullYear();
    if (!byYear.has(year)) {
      byYear.set(year, []);
    }
    byYear.get(year).push(entry);
  });

  const combined = [];
  for (const entries of byYear.values()) {
    entries.sort((a, b) => a.date - b.date);
    for (let i = 1; i < entries.length; i += 1) {
      entries[i].change = entries[i].cumulative - entries[i - 1].cumulative;
    }
    if (entries.length) {
      entries[0].change = 0;
    }
    combined.push(...entries);
  }

  return combined.sort((a, b) => a.date - b.date);
};

const buildDailyOverview = (mainSeries, theoreticalSeries, heatmapYear) => {
  const defaults = {
    currentPosition: null,
    currentPositionFormatted: '—',
    currentPositionTone: 'neutral',
    highWaterMarkFormatted: '—',
    theoreticalHWMFormatted: '—',
    theoreticalHWMTone: 'neutral',
    statusText: '—',
    statusTone: 'neutral',
    biggestWinningDayText: '—',
    biggestLosingDayText: '—',
    longestWinningStreakText: '—',
    longestLosingStreakText: '—',
    currentWinningStreak: 0,
    currentWinningStreakDescription: 'No active winning streak',
    streakIntensity: 0,
    donutSegments: [],
    heatmapEntries: [],
  };

  if (!mainSeries || mainSeries.length === 0) {
    return defaults;
  }

  const latestPoint = mainSeries[mainSeries.length - 1];
  const currentPosition = latestPoint.cumulative;
  const currentPositionTone = currentPosition > 0 ? 'positive' : currentPosition < 0 ? 'negative' : 'neutral';
  const statusText = currentPosition > 0 ? '▲ Up' : currentPosition < 0 ? '▼ Down' : '—';
  const statusTone = currentPosition > 0 ? 'positive' : currentPosition < 0 ? 'negative' : 'neutral';

  const highWaterMark = Math.max(...mainSeries.map((entry) => entry.cumulative));

  const theoreticalHWMValue =
    theoreticalSeries && theoreticalSeries.length
      ? theoreticalSeries[theoreticalSeries.length - 1].hwm
      : null;
  const theoreticalHWMTone = theoreticalHWMValue > 0 ? 'positive' : theoreticalHWMValue < 0 ? 'negative' : 'neutral';

  let biggestWinning = null;
  let biggestLosing = null;
  let winningDays = 0;
  let losingDays = 0;
  let breakEvenDays = 0;

  let currentWinningStreak = 0;
  // Track longest consecutive win/loss streaks based on cumulative comparisons
  let longestWinStreak = 0,
      currentWinStreak = 0;
  let longestLossStreak = 0,
      currentLossStreak = 0;

  for (let i = 1; i < mainSeries.length; i += 1) {
    const change = mainSeries[i].change ?? mainSeries[i].cumulative - mainSeries[i - 1].cumulative;
    if (change > 0) {
      winningDays += 1;
      if (!biggestWinning || change > biggestWinning.change) {
        biggestWinning = { date: mainSeries[i].date, change };
      }
    } else if (change < 0) {
      losingDays += 1;
      if (!biggestLosing || change < biggestLosing.change) {
        biggestLosing = { date: mainSeries[i].date, change };
      }
    } else {
      breakEvenDays += 1;
    }
  }

  // Compute longest win/loss streaks by comparing consecutive cumulative values
  for (let i = 1; i < mainSeries.length; i += 1) {
    const prev = mainSeries[i - 1].cumulative;
    const curr = mainSeries[i].cumulative;

    if (curr >= prev) {
      // Went up => winning day
      currentWinStreak++;
      longestWinStreak = Math.max(longestWinStreak, currentWinStreak);
      currentLossStreak = 0; // reset losing streak
    } else if (curr < prev) {
      // Went down => losing day
      currentLossStreak++;
      longestLossStreak = Math.max(longestLossStreak, currentLossStreak);
      currentWinStreak = 0; // reset winning streak
    } else {
      // No change => reset both streaks
      currentWinStreak = 0;
      currentLossStreak = 0;
    }
  }

  const biggestWinningDayText = biggestWinning
    ? `${formatDate(biggestWinning.date)} (${formatCurrency(biggestWinning.change)})`
    : '—';
  const biggestLosingDayText = biggestLosing
    ? `${formatDate(biggestLosing.date)} (${formatCurrency(biggestLosing.change)})`
    : '—';

  const donutSegments = [
    { label: 'Winning Days', count: winningDays, color: '#96e6ab' },
    { label: 'Losing Days', count: losingDays, color: '#df87a8' },
    { label: 'Break Even Days', count: breakEvenDays, color: '#76ace0' },
  ];

  const heatmapEntries = heatmapYear
    ? mainSeries
        .filter((entry) => entry.date.getFullYear() === heatmapYear)
        .map((entry) => ({ date: entry.date, change: entry.change ?? 0 }))
    : [];

  for (let i = mainSeries.length - 1; i >= 0; i -= 1) {
    const change =
      mainSeries[i].change ?? (i > 0 ? mainSeries[i].cumulative - mainSeries[i - 1].cumulative : 0);
    if (change > 0) {
      currentWinningStreak += 1;
    } else {
      break;
    }
  }

  const streakIntensity = Math.min(100, Math.max(0, currentWinningStreak * 25));
  const currentWinningStreakDescription =
    currentWinningStreak > 0
      ? `${currentWinningStreak} day${currentWinningStreak === 1 ? '' : 's'} on a winning streak`
      : 'No active winning streak';

  return {
    currentPosition,
    currentPositionFormatted: formatCurrency(currentPosition),
    currentPositionTone,
    highWaterMarkFormatted: formatCurrency(highWaterMark),
    theoreticalHWMFormatted: formatCurrency(theoreticalHWMValue),
    theoreticalHWMTone,
    statusText,
    statusTone,
    biggestWinningDayText,
    biggestLosingDayText,
    longestWinningStreakText: longestWinStreak ? `${longestWinStreak} day${longestWinStreak === 1 ? '' : 's'}` : '—',
    longestLosingStreakText: longestLossStreak ? `${longestLossStreak} day${longestLossStreak === 1 ? '' : 's'}` : '—',
    currentWinningStreak,
    currentWinningStreakDescription,
    streakIntensity,
    donutSegments,
    heatmapEntries,
  };
};

const OverviewPage = ({ isActive, selectedYear, currentYear, previousYear, onYearChange }) => {
  const rootRef = useRef(null);
  const [mainSeries, setMainSeries] = useState([]);
  const [theoreticalSeries, setTheoreticalSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  usePageReveal(rootRef, isActive);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      try {
        const d3 = await loadExternalScript(D3_URL, 'd3');
        if (cancelled) return;

        const parseMainDate = d3.timeParse('%d/%m/%Y');
        const parseDiscordDate = d3.timeParse('%m/%d/%y');
        const cleanCurrency = (str) => {
          if (!str || str.trim() === '') return null;
          return +str.replace(/\(/g, '-').replace(/\)/g, '').replace(/[$,]/g, '');
        };

        const [rawMainSets, rawDiscordSets] = await Promise.all([
          Promise.all(YTD_CSVS.map((url) => d3.csv(url))),
          Promise.all(DISCORD_CSVS.map((url) => d3.csv(url))),
        ]);
        const rawMain = rawMainSets.flat();
        const rawDiscord = rawDiscordSets.flat();

        const processedMain = rawMain
          .map((row) => {
            const date = row['Date'] ? parseMainDate(row['Date'].trim()) : null;
            const cumulative = cleanCurrency(row['Runnign Total']);
            if (!date || cumulative == null) return null;
            return { date, cumulative };
          })
          .filter(Boolean);
        const mainWithChanges = applyYearlyChanges(processedMain);

        const processedDiscord = rawDiscord
          .map((row) => {
            const date = row.date ? parseDiscordDate(row.date.trim()) : null;
            const hwm = cleanCurrency(row.hwm_ytd ?? row['High Water Mark']);
            if (!date || hwm == null) return null;
            return { date, hwm };
          })
          .filter(Boolean)
          .sort((a, b) => a.date - b.date);

        if (!cancelled) {
          setMainSeries(mainWithChanges);
          setTheoreticalSeries(processedDiscord);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          console.error('[OverviewPage] Failed to load overview data', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredMainSeries = useMemo(() => {
    if (selectedYear == null) return mainSeries;
    return mainSeries.filter((entry) => entry.date.getFullYear() === selectedYear);
  }, [mainSeries, selectedYear]);

  const filteredTheoreticalSeries = useMemo(() => {
    if (selectedYear == null) return theoreticalSeries;
    return theoreticalSeries.filter((entry) => entry.date.getFullYear() === selectedYear);
  }, [theoreticalSeries, selectedYear]);

  const heatmapYear = selectedYear ?? currentYear;
  const isAllTime = selectedYear == null;
  const isCurrentYear = selectedYear === currentYear;
  const resetMessage = isCurrentYear
    ? `
      <p>
        You're viewing stats for ${currentYear}. The team crushed it in ${previousYear}.
        Use the date selector above to jump back to ${previousYear} or see all-time stats.
      </p>
    `
    : `
      <p>
        You're viewing stats from ${previousYear}. The team smashed it that year.
        To see this year's run, use the date selector above to switch to ${currentYear}.
      </p>
    `;
  const chartTitle = isAllTime
    ? 'ALL TIME'
    : isCurrentYear
    ? `${selectedYear} SO FAR`
    : `${selectedYear} TOTAL`;
  const heatmapTitle = selectedYear
    ? isCurrentYear
      ? `${heatmapYear} SO FAR`
      : `${heatmapYear} TOTAL`
    : `${heatmapYear} CALENDAR`;

  const stats = useMemo(
    () => buildDailyOverview(filteredMainSeries, filteredTheoreticalSeries, heatmapYear),
    [filteredMainSeries, filteredTheoreticalSeries, heatmapYear],
  );

  if (!isActive) return null;

  return (
    <section className="app-page" ref={rootRef}>
      <PageRevealStyles />
      <LegendStyles />
      {/* Intro */}
      <div className="stats-container-three">
        <div className="stats-card-image">
          <img src="/images/vegasmatt.png" alt="Vegas Matt" />
        </div>
        <div className="stats-card-text">
          <p>
            Welcome to Vegas Matt Stats! We'll be <b>FIRING IT IN THERE</b> all year. You can keep up to date with the latest{' '}
            <b>INNAGE</b>,  <b>WINNAGE</b> and <b>SIDE BETS</b> here, at <b>OFFICIALVEGASMATTSTATS.COM</b>
          </p>
        </div>
      </div>
      {!isAllTime && (
        <InfoBar type="warning" title={`${currentYear} reset is live`} html={resetMessage} animate={false} />
      )}
     {/* NEW INFO/WARNING BOX */}
      <InfoBar
        type="info"
        title="About the data"
        html={`
          <p>
            This is the official tracker of the Vegas Matt Stats that the team post on their YouTube channel. The stats shown on this page are derived from the data on YouTube and the data recorded by the amazing team in the Vegas Matt
            <a href='https://discord.gg/vegasmatt'> Discord.</a> Please watch and subscribe to the Vegas Matt YouTube channel:
            <a href='https://www.youtube.com/vegasmatt' target='_blank' rel='noopener noreferrer'>
              https://www.youtube.com/vegasmatt
            </a>.
          </p>
        `}
      />
      <TsunamiMeter
        intensity={stats.streakIntensity}
        currentStreak={stats.currentWinningStreak}
        streakDescription={stats.currentWinningStreakDescription}
      />

    

 
      {/* END NEW INFO/WARNING BOX */}

      {/* Top row of summary stats */}
      <div className="stats-container" id="summaryStats">
        <StatCard
          title="Current Position"
          value={loading ? 'Loading…' : stats.currentPositionFormatted}
          tone={stats.currentPositionTone}
        />
        <StatCard
          title="High Water Mark"
          value={loading ? 'Loading…' : stats.highWaterMarkFormatted}
          tone="positive"
        />
        <StatCard
          title="Current Status"
          value={loading ? 'Loading…' : stats.statusText}
          tone={stats.statusTone}
        />
        <StatCard
          title="Theoretical High Water Mark"
          value={loading ? 'Loading…' : stats.theoreticalHWMFormatted}
          tone={stats.theoreticalHWMTone}
        />
      </div>

      {/* Main cumulative chart */}
      <div className="chart-container" id="plot">
        <h2>{chartTitle}</h2>
        <div className="chart-legend">
          <div className="legend-item">
            <div style={{ width: 15, height: 15, backgroundColor: '#96e6ab', border: '1px solid #ccc', marginRight: 5 }} />
            <span>Theoretical High Water Mark</span>
          </div>
          <div className="legend-item">
            <div style={{ width: 15, height: 15, backgroundColor: '#76ace0', border: '1px solid #ccc', marginRight: 5 }} />
            <span>Current Position</span>
          </div>
        </div>
        {error ? (
          <p style={{ color: '#df87a8' }}>Failed to load chart data. Please try again later.</p>
        ) : (
          <OverviewLineChart mainSeries={filteredMainSeries} theoreticalSeries={filteredTheoreticalSeries} />
        )}
      </div>

      {/* Extra message with image and text */}
      <div className="stats-container-three" id="winningStreaks">
        <div className="stats-card-image">
          <img src="/images/WBG.png" alt="WBG" />
        </div>
        <div className="stats-card-text">
          <p>
            <b>WINNING STREAKS</b> and <b>LOSING STREAKS</b> always go on way longer than you could possibly imagine.
          </p>
        </div>
      </div>

      {/* Extra stats and donut chart */}
      <div className="extra-stats-container">
        <div className="stats-column">
          <StatCard
            title="Biggest Winning Day"
            value={loading ? 'Loading…' : stats.biggestWinningDayText}
            tone="positive"
          />
          <StatCard
            title="Biggest Losing Day"
            value={loading ? 'Loading…' : stats.biggestLosingDayText}
            tone="negative"
          />
        </div>
        <div className="chart-card">
          <h2>WINNING AND LOSING DAYS</h2>
          {error ? (
            <p style={{ color: '#df87a8' }}>Unable to load breakdown.</p>
          ) : (
            <WinningLosingDonut segments={stats.donutSegments} />
          )}
        </div>
        <div className="stats-column">
          <StatCard
            title="Longest Winning Streak"
            value={loading ? 'Loading…' : stats.longestWinningStreakText}
            tone="positive"
          />
          <StatCard
            title="Longest Losing Streak"
            value={loading ? 'Loading…' : stats.longestLosingStreakText}
            tone="negative"
          />
        </div>
      </div>

      <div
        className="chart-container"
        id="calendarHeatmap"
        style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <div className="chart-header">
          <h2>{heatmapTitle}</h2>
          <div className="chart-header__actions">
            {selectedYear !== previousYear ? (
              <button
                type="button"
                className="year-filter__button"
                onClick={() => onYearChange && onYearChange('previous')}
              >
                View {previousYear}
              </button>
            ) : (
              <button
                type="button"
                className="year-filter__button"
                onClick={() => onYearChange && onYearChange('current')}
              >
                Back to {currentYear}
              </button>
            )}
          </div>
          {isAllTime ? (
            <div className="chart-header__note">Calendar view is yearly. Showing {heatmapYear}.</div>
          ) : null}
        </div>
        <div className="chart-legend chart-legend--inline">
          <div className="legend-item">
            <div style={{ width: 15, height: 15, backgroundColor: '#96e6ab', border: '1px solid #ccc' }} />
            <span>Win</span>
          </div>
          <div className="legend-item">
            <div style={{ width: 15, height: 15, backgroundColor: '#df87a8', border: '1px solid #ccc' }} />
            <span>Lose</span>
          </div>
          <div className="legend-item">
            <div style={{ width: 15, height: 15, backgroundColor: '#76ace0', border: '1px solid #ccc' }} />
            <span>Even</span>
          </div>
        </div>
        {error ? (
          <p style={{ color: '#df87a8' }}>Unable to load heatmap.</p>
        ) : (
          <CalendarHeatmap entries={stats.heatmapEntries} year={heatmapYear} />
        )}
      </div>
    </section>
  );
};

export default OverviewPage;
